import { ArrowRightOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { Capacitor } from '@capacitor/core';
import { useRequest } from 'ahooks';
import { Alert, Upload } from 'antd';
import useNotification from 'antd/es/notification/useNotification';
import { AxiosError } from 'axios';
import ConversationTopBar from 'components/chats/ConversationTopBar';
import PatientDocumentsList from 'components/lists/PatientDocumentsList';
import PaywallModal from 'components/modals/PaywallModal';
import useSelectConversations from 'hooks/useSelectConversations';
import posthog from 'posthog-js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ApiPatientDocumentsManager, { PatientDocument } from 'services/api/ApiPatientDocumentsManager';
import { selectDoesUserHaveFullAccess } from 'store/users/selectors';
import { Button } from 'components/ui';
import { colors, shadows } from 'design';

// Document validation error codes from the backend serializer
const DOCUMENT_VALIDATION_ERROR_CODES = ['INVALID_FILE_TYPE', 'FILE_TOO_LARGE', 'DAILY_UPLOAD_LIMIT_REACHED'] as const;

/**
 * Extracts error code from DRF error response.
 * DRF stores codes in ErrorDetail objects: {"field": [{"code": "...", "message": "..."}]}
 * or {"non_field_errors": [{"code": "...", "message": "..."}]}
 */
function extractErrorCode(errorData: any): string | undefined {
  if (!errorData || typeof errorData === 'string') {
    return undefined;
  }

  if (errorData.field && errorData.field.code) {
    // for errors related to the `file` field
    return errorData.field.code;
  }

  if (errorData.code && Array.isArray(errorData.code)) {
    // for generic errors; e.g. the one for the daily upload limit
    return errorData.code[0];
  }

  return undefined;
}

const useStyles = createUseStyles({
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: 'auto',
    padding: 16,
    backgroundColor: colors.surface.page,
  },
  section: {
    marginBottom: 24,
    maxWidth: 600,
    width: '100%',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 12,
    color: colors.text.primary,
  },
  uploadButtonWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadButton: {
    marginBottom: 16,
    width: '100%',
    // antd buttons default to `white-space: nowrap`, so a long caregiver name
    // overflows the fixed-width button instead of wrapping, which is what was
    // pushing the whole page into horizontal scroll.
    whiteSpace: 'normal',
  },
  emptyState: {
    textAlign: 'center',
    padding: 40,
    color: colors.text.placeholder,
  },
  infoCallout: {
    marginBottom: 16,
  },
  paywallButtonContainer: {
    padding: 10,
    backgroundColor: colors.surface.page,
    marginTop: 16,
    marginBottom: 32,
  },
  paywallButton: {
    boxShadow: shadows.xs,
  },
  paywallButtonDiscover: {
    textDecoration: 'underline',
  },
});

export default function PatientDocumentsPage() {
  const styles = useStyles();
  const { t } = useTranslation();
  const [api, context] = useNotification();
  const navigate = useNavigate();
  const hasFullAccess = useSelector(selectDoesUserHaveFullAccess);
  const { conversationMetadata } = useSelectConversations();
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);

  const currentCaregiver = conversationMetadata?.conversation.current_owner;

  const {
    data: documents,
    loading,
    refresh,
  } = useRequest(() => ApiPatientDocumentsManager.list({}), {
    onError: error => {
      console.error('[PatientDocumentsPage] Error loading documents:', error);
      api.error({
        message: t('documents.errors.loadError'),
      });
    },
  });

  const { loading: uploading, run: uploadDocument } = useRequest(
    (file: File) =>
      ApiPatientDocumentsManager.upload({
        file,
        caregiverId: currentCaregiver?.id,
      }),
    {
      manual: true,
      onSuccess: () => {
        if (currentCaregiver) {
          api.success({
            message: t('documents.uploadAndShareSuccess', {
              caregiverName: currentCaregiver.display_name,
            }),
          });
        } else {
          api.success({
            message: t('documents.uploadSuccess'),
          });
        }
        refresh();
      },
      onError: error => {
        console.error('[PatientDocumentsPage] Error uploading document:', error);
        let errorMessage = t('documents.errors.uploadError');

        if (error instanceof AxiosError) {
          const errorCode = extractErrorCode(error.response?.data);
          if (errorCode && DOCUMENT_VALIDATION_ERROR_CODES.includes(errorCode as any)) {
            const translatedError = t(`documents.errors.${errorCode}` as any);
            // Only use the translated error if it's not the same as the key (meaning translation exists)
            if (translatedError !== `documents.errors.${errorCode}`) {
              errorMessage = translatedError;
            }
          }
        }

        api.error({ message: errorMessage });
      },
    },
  );

  const handleUpload = async (file: File): Promise<boolean> => {
    posthog.capture('patient_documents.document_upload_started');
    // Client-side validation
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.pdf'];
    const maxSize = 30 * 1024 * 1024; // 30MB

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      api.error({
        message: t('documents.errors.invalidType'),
      });
      return false;
    }

    if (file.size > maxSize) {
      api.error({
        message: t('documents.errors.fileTooLarge'),
      });
      return false;
    }

    // On Android/Capacitor, read the file into memory to prevent ERR_UPLOAD_FILE_CHANGED
    // This ensures we have a stable file reference
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: file.type });
        const stableFile = new File([blob], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
        uploadDocument(stableFile);
      } catch (error) {
        console.error('[PatientDocumentsPage] Error reading file:', error);
        api.error({
          message: t('documents.errors.uploadError'),
        });
        return false;
      }
    } else {
      uploadDocument(file);
    }
    return false; // Prevent default upload
  };

  const isSharedWithCurrentCaregiver = (document: PatientDocument): boolean => {
    if (!currentCaregiver) return false;
    return document.shared_with_caregivers.includes(currentCaregiver.id);
  };

  // Split documents into shared and not shared
  const sharedDocuments = documents?.filter(doc => isSharedWithCurrentCaregiver(doc)) || [];
  const notSharedDocuments = documents?.filter(doc => !isSharedWithCurrentCaregiver(doc)) || [];

  return (
    <div className={styles.pageContainer}>
      {context}
      <ConversationTopBar
        showBackButton
        avatar={conversationMetadata?.conversation.current_owner?.photo_url}
        name={conversationMetadata?.conversation.current_owner?.display_name}
        onBackButtonClick={() => navigate(-1)}
      />
      <div className={styles.container}>
        {loading && documents === undefined ? (
          <div className={styles.emptyState}>{t('common.loading')}</div>
        ) : (
          <>
            {/* Shared Documents Section */}
            <div className={styles.section}>
              {(sharedDocuments.length > 0 || notSharedDocuments.length > 0) && (
                <div className={styles.sectionTitle}>
                  {currentCaregiver
                    ? t('documents.sharedWith', {
                        caregiverName: currentCaregiver.display_name,
                      })
                    : t('documents.shared')}
                </div>
              )}
              {sharedDocuments.length === 0 ? (
                <div className={styles.emptyState}>{t('documents.noSharedDocuments')}</div>
              ) : (
                <PatientDocumentsList
                  documents={sharedDocuments}
                  isCaregiver={false}
                  currentCaregiver={currentCaregiver}
                  onRefresh={refresh}
                />
              )}
              {/* Information callout when documents exist but none are shared with current caregiver */}
              {currentCaregiver && documents && documents.length > 0 && sharedDocuments.length === 0 && (
                <Alert
                  message={t('documents.reshareInfo')}
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  className={styles.infoCallout}
                />
              )}
              <div className={styles.uploadButtonWrapper}>
                {hasFullAccess ? (
                  <Upload
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    beforeUpload={handleUpload}
                    showUploadList={false}
                    disabled={uploading}
                    style={{ padding: '0px 12px' }}
                  >
                    <Button
                      size="lg"
                      icon={<UploadOutlined />}
                      loading={uploading}
                      className={styles.uploadButton}
                      style={{
                        color: colors.brand.accentStrong,
                        height: 'auto',
                        minHeight: 64,
                        paddingTop: 12,
                        paddingBottom: 12,
                        marginTop: 16,
                        marginBottom: 32,
                      }}
                    >
                      {currentCaregiver
                        ? t('documents.uploadWithCaregiver', {
                            caregiverName: currentCaregiver.display_name,
                          })
                        : t('documents.upload')}
                    </Button>
                  </Upload>
                ) : (
                  <div className={styles.paywallButtonContainer}>
                    <Alert
                      className={styles.paywallButton}
                      message={
                        <>
                          {t('documents.paywall.uploadOnlyForPaidUsers')} <ArrowRightOutlined />
                        </>
                      }
                      type="info"
                      showIcon
                      onClick={() => {
                        setIsPaywallOpen(true);
                        posthog.capture('paywall.opened', { source: 'documents' });
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Not Shared Documents Section */}
            {currentCaregiver && notSharedDocuments.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>{t('documents.notShared')}</div>
                <PatientDocumentsList
                  documents={notSharedDocuments}
                  isCaregiver={false}
                  currentCaregiver={currentCaregiver}
                  onRefresh={refresh}
                />
              </div>
            )}
          </>
        )}
      </div>
      <PaywallModal
        isOpen={isPaywallOpen}
        onClose={() => setIsPaywallOpen(false)}
        closeModal={() => setIsPaywallOpen(false)}
        suggestTestIfEligible={false}
      />
    </div>
  );
}
